/**
 * Returns a copy of object array after merge and unique by specified field name.
 *
 * @param srcObj original array of objects
 * @param destObj updated array of objects
 * @param key field name to unique with
 * @returns merged and unique object array
 */
export const getMergeAndUniqObjectArrayByKey = <T>(srcObj: T[], destObj: T[], key: keyof T) => {
  const mergedArray = [...srcObj, ...destObj];
  return uniqueObjectArrayByProp(mergedArray, key);
};

/**
 * Returns a unqiue array of objects based on a key property.
 *
 * This will be more time efficient, O(n) than sorting the list, O(nlogn) at the expense of being
 * less memory efficient, O(n).
 *
 * @param array - An array of objects.
 * @param key - The property to be used to determine uniqueness. It should be a property of the object.
 */
export const uniqueObjectArrayByProp = <T>(array: T[], key: keyof T): T[] => {
  const groupByKeyMap = array.reduce((map, item) => map.set(item[key], item), new Map());
  return Array.from(groupByKeyMap.values());
};

/**
 * Checks if any object is an empty object.
 * This method was created to avoid using the same method from lodash.
 * Implementation from https://github.com/you-dont-need/You-Dont-Need-Lodash-Underscore?tab=readme-ov-file#_isempty
 *
 * @param obj - The object to check.
 */
export const isEmpty = (obj: any): boolean =>
  [Object, Array].includes(obj?.constructor) && !Object.entries(obj ?? {}).length;
